<script lang="ts" setup>
import vuIcon from '@ds/vuekit/src/components/vu-icon.vue';
import type { HomepageStep } from '~/models/homepage';
import useI18NAsync from '~/composables/i18nAsync';

interface Props {
  isMobileDevice?: boolean;
  steps: HomepageStep[];
}

const emit = defineEmits(['clickOnStep']);
const props = withDefaults(defineProps<Props>(), {
  isMobileDevice: false,
  HomepageStep: [] as HomepageStep[],
});
const i18nSuspense = useI18NAsync('homepage');
</script>
<template>
  <div
    class="home-page-stepper flex flex-row align-items-center justify-space-around flex-wrap--sm py-10px grid-gap-30px--sm"
    style="width: 100%"
    @click="emit('clickOnStep')"
  >
    <div
      v-for="(step, i) in steps"
      :key="`${i}-step`"
      class="step flex grid-gap-10px--sm align-items-center justify-center flex-column--sm relative"
    >
      <div class="relative">
        <span
          :class="['index', { 'align-items-center flex justify-center': props.isMobileDevice }]"
        >
          {{ i + 1 }} {{ props.isMobileDevice ? '' : '.' }}
        </span>
        <vu-icon
          :icon="step.icon"
          :class="[
            'icon',
            { 'align-items-center flex-important justify-center': props.isMobileDevice },
          ]"
        />
      </div>
      <i18nSuspense class="text" :ikey="step.name" tag="span" />
    </div>
  </div>
</template>
<style lang="scss" scoped>
@use '/assets/scss/variables' as variables;

.home-page-stepper {
  background-color: var(--grey-5);
  padding-left: 26px;
  height: 120px;
  overflow: hidden;

  @include variables.for-sm {
    padding: 30px;
    height: unset;
    overflow: hidden;
    background-color: var(--grey-2);
  }
}

.step {
  clip-path: polygon(0 0, calc(100% - 30px) 0, 100% 50%, calc(100% - 30px) 100%, 0 100%, 30px 50%);
  background: var(--grey-3);
  background: linear-gradient(90deg, var(--grey-3) 0%, #fff 100%);
  width: 100%;
  color: var(--grey-7);
  text-transform: uppercase;
  height: 120px;
  margin-left: -26px;

  @include variables.for-sm {
    clip-path: unset;
    height: 120px;
    background: unset;
    margin-left: unset;
  }
}

.step:first-child {
  clip-path: polygon(0 0, calc(100% - 30px) 0, 100% 50%, calc(100% - 30px) 100%, 0 100%);

  @include variables.for-sm {
    clip-path: unset;
  }
}

.step:last-child {
  clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%, 30px 50%);

  @include variables.for-sm {
    clip-path: unset;
  }
}

.home-page-stepper :deep(.text) {
  font-size: 22px;
  line-height: 22px;
  font-weight: 600;
  text-align: center;

  @include variables.for-mid {
    font-size: 14px;
    line-height: 14px;
  }
  @include variables.for-sm {
    font-size: 14px;
    line-height: 14px;
  }
}

.index {
  font-size: 36px;
  padding-top: 24px;
  font-weight: bold;

  @include variables.for-mid {
    font-size: 16px;
  }

  @include variables.for-sm {
    font-size: 20px;
    line-height: 20px;
    position: absolute;
    padding-top: 0;
    top: -5px;
    left: -5px;
    background-color: white;
    border-radius: 50px;
    border: 1px solid var(--blue-2);
    height: 30px;
    width: 30px;
  }
}

.icon {
  margin-left: 6px;
  margin-right: 15px;
  font-size: 46px;
  color: #3d3d3d !important;

  @include variables.for-sm {
    margin: 0;
    background-color: var(--blue-2);
    width: 74px;
    height: 74px;
    min-height: 74px;
    border-radius: 74px;
    color: white !important;
    font-size: 28px;
  }
}
</style>
